/**
 * AutoScrollingContent
 * Moves logo/stat theme blocks into slots and builds seamless marquee tracks.
 * Mobile: first half → top row, second half → bottom row (ceil to top when odd).
 * When a row's content fits the viewport, animation is disabled and logos stay centered.
 *
 * Loop geometry: two `.auto-scrolling-content__marquee-set` siblings, gap + trailing
 * padding on each set (none on the track). Track width = 2 × set width, so CSS
 * `translateX(-50%)` is exactly one visual cycle. A flat flex row with `gap` would
 * make -50% miss by gap/2 at the seam.
 */
class AutoScrollingContent extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ascMounted === 'true') return;

    this.blockSource = this.querySelector('[data-asc-block-source]');
    this.marqueeHost = this.querySelector('[data-asc-marquee]');
    this.statsHost = this.querySelector('[data-asc-stats]');

    if (!this.blockSource || !this.marqueeHost || !this.statsHost) return;

    this.logos = Array.from(this.blockSource.querySelectorAll('[data-asc-logo]'));
    this.stats = Array.from(this.blockSource.querySelectorAll('[data-asc-stat]'));

    this.placeStats();
    this.buildMarquee();
    this.onResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.dataset.ascMounted = 'true';
  }

  disconnectedCallback() {
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
    this.returnLogosToSource();
    this.dataset.ascMounted = 'false';
  }

  /** @returns {boolean} */
  isMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  placeStats() {
    this.stats.forEach((stat) => {
      this.statsHost.appendChild(stat);
    });
  }

  returnLogosToSource() {
    if (!this.blockSource || !this.logos) return;
    this.logos.forEach((logo) => {
      this.blockSource.appendChild(logo);
    });
  }

  handleResize() {
    const nextMobile = this.isMobile();
    if (nextMobile !== this.wasMobile) {
      this.wasMobile = nextMobile;
      this.buildMarquee();
      return;
    }
    this.syncMarqueeOverflow();
  }

  buildMarquee() {
    if (!this.marqueeHost) return;

    this.returnLogosToSource();
    this.marqueeHost.replaceChildren();
    if (this.logos.length === 0) return;

    const rowsWrap = document.createElement('div');
    rowsWrap.className = 'auto-scrolling-content__marquee-rows';

    const mobile = this.isMobile();
    this.wasMobile = mobile;

    if (!mobile) {
      rowsWrap.appendChild(this.createRow(this.logos, true));
    } else {
      const splitIndex = Math.ceil(this.logos.length / 2);
      const topLogos = this.logos.slice(0, splitIndex);
      const bottomLogos = this.logos.slice(splitIndex);
      rowsWrap.appendChild(this.createRow(topLogos, true));
      if (bottomLogos.length > 0) {
        rowsWrap.appendChild(this.createRow(bottomLogos, false));
      }
    }

    this.marqueeHost.appendChild(rowsWrap);
    this.syncMarqueeOverflow();
    this.marqueeHost.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => this.syncMarqueeOverflow(), { once: true });
    });
  }

  /**
   * Disable looping animation when one set of logos fits inside the row.
   * Side fade masks stay only while at least one row is still scrolling.
   */
  syncMarqueeOverflow() {
    if (!this.marqueeHost) return;

    let hasScrollingTrack = false;

    this.marqueeHost.querySelectorAll('.auto-scrolling-content__marquee-row').forEach((row) => {
      const track = row.querySelector('.auto-scrolling-content__marquee-track');
      if (!track) return;

      let firstSet = track.querySelector('.auto-scrolling-content__marquee-set');
      if (!firstSet) return;

      if (track.children.length < 2) {
        track.classList.remove('auto-scrolling-content__marquee-track--static');
        track.appendChild(this.cloneSet(firstSet));
        firstSet = track.querySelector('.auto-scrolling-content__marquee-set');
      }

      track.classList.remove('auto-scrolling-content__marquee-track--static');

      const setWidth = firstSet.offsetWidth;
      const fits = setWidth <= row.clientWidth + 1;

      if (fits) {
        while (track.children.length > 1) {
          track.lastElementChild.remove();
        }
        track.classList.add('auto-scrolling-content__marquee-track--static');
      } else {
        hasScrollingTrack = true;
      }
    });

    this.marqueeHost.classList.toggle(
      'auto-scrolling-content__marquee--static',
      !hasScrollingTrack
    );
  }

  /**
   * @param {Element[]} logos
   * @param {boolean} adoptOriginals - move live nodes into the first copy
   * @returns {HTMLElement}
   */
  createRow(logos, adoptOriginals) {
    const row = document.createElement('div');
    row.className = 'auto-scrolling-content__marquee-row';

    const track = document.createElement('div');
    track.className = 'auto-scrolling-content__marquee-track';

    if (logos.length === 0) {
      track.classList.add('auto-scrolling-content__marquee-track--static');
      row.appendChild(track);
      return row;
    }

    track.appendChild(this.createSet(logos, adoptOriginals));
    track.appendChild(this.createSet(logos, false));

    row.appendChild(track);
    return row;
  }

  /**
   * @param {Element[]} logos
   * @param {boolean} adoptOriginals
   * @returns {HTMLElement}
   */
  createSet(logos, adoptOriginals) {
    const set = document.createElement('div');
    set.className = 'auto-scrolling-content__marquee-set';

    if (!adoptOriginals) {
      set.setAttribute('aria-hidden', 'true');
    }

    logos.forEach((logo) => {
      if (adoptOriginals) {
        set.appendChild(logo);
      } else {
        set.appendChild(this.cloneLogo(logo));
      }
    });

    return set;
  }

  /**
   * @param {Element} set
   * @returns {Element}
   */
  cloneSet(set) {
    const clone = set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    return clone;
  }

  /**
   * @param {Element} logo
   * @returns {Element}
   */
  cloneLogo(logo) {
    const clone = logo.cloneNode(true);
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    return clone;
  }
}

if (!customElements.get('auto-scrolling-content')) {
  customElements.define('auto-scrolling-content', AutoScrollingContent);
}
